import { Button, Dialog } from "@buildblockinc/buildblock-design-system";
import { Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const CIRCLE_SIZE = 80;
const ANIMATION_DURATION = 500;

function easeInOut(t: number) {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
}

interface DownloadProgressDialogProps {
  open: boolean;
  status: "progress" | "success" | "error";
  progress: number;
  onCancel: () => void;
  onDownload: () => void;
  onRetry: () => void;
  onDone: () => void;
}

export function DownloadProgressDialog({
  open,
  status,
  progress,
  onCancel,
  onDownload,
  onRetry,
  onDone,
}: DownloadProgressDialogProps) {
  const [displayDeg, setDisplayDeg] = useState(0);
  const displayDegRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (status !== "progress") return;

    const targetDeg = (Math.min(100, Math.max(0, progress)) / 100) * 360;
    const startDeg = displayDegRef.current;

    if (startDeg === targetDeg) return;
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    const startTime = performance.now();

    const tick = (now: number) => {
      const t = Math.min((now - startTime) / ANIMATION_DURATION, 1);
      const deg = startDeg + (targetDeg - startDeg) * easeInOut(t);
      displayDegRef.current = deg;
      setDisplayDeg(deg);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [progress, status]);

  const clampedProgress = Math.min(100, Math.max(0, progress));

  return (
    <Dialog open={open} onClose={status === "progress" ? onCancel : onDone}>
      <Dialog.Content>
        <div className="flex flex-col gap-6 items-center px-4 pt-8 pb-4">
          {status === "progress" && (
            <>
              <div
                className="rounded-circle shrink-0"
                aria-hidden="true"
                style={{
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  background: `conic-gradient(from 0deg, #D6D6D6 ${displayDeg}deg, #FFFFFF ${displayDeg}deg)`,
                }}
              />
              <p className="text-center text-gray-900 text-body1">
                Preparing ZIP...{Math.round(clampedProgress)}% downloaded
              </p>
              <Button type="button" color="secondary" className="w-full" onClick={onCancel}>
                Cancel
              </Button>
            </>
          )}

          {status === "success" && (
            <>
              <Check className="text-green-800" size={40} strokeWidth={2.5} />

              <p className="text-center text-gray-900 text-body1">ZIP file ready to download</p>
              <Button type="button" variant="fill" color="info" className="w-full" onClick={onDownload}>
                Download ZIP file
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <X className="text-red-500" size={40} strokeWidth={2.5} />

              <p className="text-center text-gray-900 whitespace-pre-line text-body1">
                {"Download failed.\nPlease check your connection."}
              </p>
              <div className="flex gap-2 w-full">
                <Button type="button" variant="ghost" color="secondary" className="flex-1" onClick={onDone}>
                  Done
                </Button>
                <Button type="button" variant="fill" color="info" className="flex-1" onClick={onRetry}>
                  Retry
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
